import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

// Reusable luxury category card used on:
//   1. The homepage "Shop by Category" strip (below Hero)
//   2. The shop landing state (inside Collections.jsx, before a category is picked)
//
// Design brief (per merchant): large image · rounded corners · soft shadow ·
// black gradient overlay · white serif typography · gold hover border ·
// subtle zoom on hover · fully clickable · mobile-responsive.

export default function CategoryCard({
  label,
  image,
  onClick,
  testId,
  size = "md",       // "sm" | "md" | "lg" — controls typography scale
  aspect = "4/5",   // Tailwind aspect ratio string
}) {
  const titleSize =
    size === "lg"
      ? "text-2xl sm:text-3xl md:text-4xl"
      : size === "sm"
        ? "text-lg sm:text-xl"
        : "text-xl sm:text-2xl md:text-3xl";
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
      className={`group relative block w-full text-left overflow-hidden rounded-2xl border border-[#2A2A2A] hover:border-[#D4AF37] bg-[#0A0A0A] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.8)] transition-all duration-500`}
      style={{ aspectRatio: aspect }}
      data-testid={testId}
      aria-label={`Shop ${label}`}
    >
      {/* Image (or fallback placeholder) */}
      {image ? (
        <img
          src={image}
          alt={label}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.08]"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#151515] via-[#0F0F0F] to-black" />
      )}

      {/* Black gradient overlay — heavier at the bottom so text pops */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/20 group-hover:from-black/75 transition-colors duration-500" />

      {/* Gold hairline that appears on hover */}
      <div className="absolute inset-3 rounded-xl border border-transparent group-hover:border-[#D4AF37]/50 transition-colors duration-500 pointer-events-none" />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-6 md:p-8">
        <div className="text-[0.6rem] sm:text-[0.65rem] tracking-[0.32em] uppercase text-[#D4AF37] mb-2 font-semibold">
          Shop
        </div>
        <div className="flex items-end justify-between gap-3">
          <div
            className={`font-serif text-white leading-tight ${titleSize} drop-shadow-[0_4px_18px_rgba(0,0,0,0.6)]`}
          >
            {label}
          </div>
          <div className="shrink-0 w-10 h-10 rounded-full border border-[#D4AF37]/40 group-hover:border-[#D4AF37] flex items-center justify-center text-[#D4AF37] transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1">
            <ArrowUpRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </motion.button>
  );
}
